<script setup lang="ts">
/**
 * Customer avatar: the platform profile picture when there is one (api/app/profiles.py), otherwise the initial
 * circle the inbox always had. A picture that fails to load (expired Meta link) falls back to the initial and asks
 * the API for a fresh profile; the new link arrives by WebSocket (conversation.updated) and replaces it.
 */
import { computed, ref, watch } from 'vue'
import { reportBrokenAvatar } from '../../lib/avatar'

const props = withDefaults(
  defineProps<{
    initial: string
    url?: string | null
    /** contacts.id — needed to ask for a fresh picture; null for LINE groups. */
    contactId?: string | null
    /** px; omitted = inherit `--av-size` from the parent's CSS (e.g. smaller rows on narrow screens), default 36 */
    size?: number | null
    /** Class of the fallback circle (base.css `.av-m` = the customer green). */
    tone?: string
  }>(),
  { url: null, contactId: null, size: null, tone: 'av-m' },
)

const failed = ref(false)
watch(
  () => props.url,
  () => {
    failed.value = false
  },
)
const showImg = computed(() => !!props.url && !failed.value)
const sizeVar = computed(() => (props.size ? { '--av-size': `${props.size}px` } : undefined))

function onError(): void {
  failed.value = true
  if (props.contactId) reportBrokenAvatar(props.contactId)
}
</script>

<template>
  <span
    class="av cav"
    :class="showImg ? 'has-img' : tone"
    :style="sizeVar"
    aria-hidden="true"
  >
    <img
      v-if="showImg"
      :src="url ?? undefined"
      alt=""
      loading="lazy"
      decoding="async"
      referrerpolicy="no-referrer"
      draggable="false"
      @error="onError"
    />
    <template v-else>{{ initial }}</template>
  </span>
</template>

<style scoped>
.cav {
  width: var(--av-size, 36px);
  height: var(--av-size, 36px);
  font-size: max(12px, calc(var(--av-size, 36px) * 0.39));
  overflow: hidden;
}
.cav.has-img {
  background: #e6eeea;
}
.cav img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
